import React, { useCallback } from "react";
import PropTypes from "prop-types";
import InputDatePicker from "../../../../EvaluationNew/components/InputDatePicker";
import { displayDateUS, formatDateForInput } from "../../../../../utils/dateUtils";
import { PATIENT_THEME } from "../../../patientTheme";
import { DeleteIcon, PlusIcon } from "../../../../../Icons";
import {
  CheckboxGrid,
  ExclusiveChoiceGrid,
  Field,
  SubHeading,
  checkboxClass,
  fieldClass,
  textareaClass,
} from "../formLayout";
import {
  DRAINAGE_AMOUNT_OPTIONS,
  DRAINAGE_TYPE_OPTIONS,
  INCISION_CLOSED_WITH_OPTIONS,
  MAX_WOUNDS,
  NPWT_FOAM_OPTIONS,
  NPWT_PERIWOUND_OPTIONS,
  NPWT_PRESSURE_MODE_OPTIONS,
  NPWT_WOUND_BED_OPTIONS,
  SURROUNDING_TISSUE_OPTIONS,
  WOUND_BED_TISSUE_OPTIONS,
  WOUND_STAGE_OPTIONS,
  WOUND_TYPE_OPTIONS,
  WOUND_YES_NO_OPTIONS,
  computeWoundAreaSqCm,
  emptyWoundEntry,
  emptyIdentifiedWoundFields,
  toggleInWoundList,
} from "./woundAssessmentContent";
import {
  WOUND_MAP_BACK_REGIONS,
  WOUND_MAP_FRONT_REGIONS,
  getWoundMapRegion,
  toggleWoundMapSite,
  woundLocationFromSites,
} from "./woundMapRegions";

const SILHOUETTE_FILL = "#e2e8f0";
const SILHOUETTE_STROKE = "#94a3b8";
const MARKER_FILL = "#dc2626";
const MARKER_RING = "#fee2e2";
const HOTSPOT_IDLE = "rgba(59, 130, 246, 0.12)";
const HOTSPOT_HOVER = "rgba(59, 130, 246, 0.28)";

const selectClass = `${fieldClass} bg-white`;
const EMPTY_LIST = [];

const BodyOutline = () => (
  <g aria-hidden>
    <ellipse
      cx="60"
      cy="24"
      rx="18"
      ry="22"
      fill={SILHOUETTE_FILL}
      stroke={SILHOUETTE_STROKE}
      strokeWidth="1.5"
    />
    <path
      d="M35 46 L85 46 L78 130 L42 130 Z"
      fill={SILHOUETTE_FILL}
      stroke={SILHOUETTE_STROKE}
      strokeWidth="1.5"
    />
    <path
      d="M35 50 L15 100 L22 105 L38 58 Z"
      fill={SILHOUETTE_FILL}
      stroke={SILHOUETTE_STROKE}
      strokeWidth="1.5"
    />
    <path
      d="M85 50 L105 100 L98 105 L82 58 Z"
      fill={SILHOUETTE_FILL}
      stroke={SILHOUETTE_STROKE}
      strokeWidth="1.5"
    />
    <path
      d="M42 130 L35 240 L48 240 L55 130 Z"
      fill={SILHOUETTE_FILL}
      stroke={SILHOUETTE_STROKE}
      strokeWidth="1.5"
    />
    <path
      d="M78 130 L85 240 L72 240 L65 130 Z"
      fill={SILHOUETTE_FILL}
      stroke={SILHOUETTE_STROKE}
      strokeWidth="1.5"
    />
  </g>
);

const InteractiveBodyFigure = ({
  label,
  regions,
  selectedIds,
  markerIndexById,
  canEdit,
  printMode,
  onToggle,
}) => {
  const interactive = canEdit && !printMode;

  return (
    <div className="flex flex-col items-center shrink-0">
      <div className="text-[9px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
        {label}
      </div>
      <svg
        viewBox="0 0 120 280"
        className="w-full max-w-[156px] h-auto select-none"
        role="img"
        aria-label={`${label} body map — click to mark wound locations`}
      >
        <BodyOutline />
        {regions.map((region) => {
          const selected = selectedIds.includes(region.id);
          const markerNum = markerIndexById.get(region.id);
          const r = region.r ?? 10;
          return (
            <g key={region.id}>
              <circle
                cx={region.cx}
                cy={region.cy}
                r={r}
                fill={selected ? "rgba(220, 38, 38, 0.18)" : HOTSPOT_IDLE}
                stroke={selected ? MARKER_FILL : "transparent"}
                strokeWidth={selected ? 1.5 : 0}
                className={interactive ? "cursor-pointer" : undefined}
                style={interactive ? { transition: "fill 120ms ease" } : undefined}
                onMouseEnter={
                  interactive
                    ? (e) => {
                        if (!selected) e.currentTarget.setAttribute("fill", HOTSPOT_HOVER);
                      }
                    : undefined
                }
                onMouseLeave={
                  interactive
                    ? (e) => {
                        if (!selected)
                          e.currentTarget.setAttribute("fill", HOTSPOT_IDLE);
                      }
                    : undefined
                }
                onClick={
                  interactive
                    ? (e) => {
                        e.preventDefault();
                        onToggle(region.id);
                      }
                    : undefined
                }
              >
                <title>{region.label}</title>
              </circle>
              {selected && markerNum != null ? (
                <g pointerEvents="none">
                  <circle
                    cx={region.cx}
                    cy={region.cy}
                    r={9}
                    fill={MARKER_FILL}
                    stroke={MARKER_RING}
                    strokeWidth="2"
                  />
                  <text
                    x={region.cx}
                    y={region.cy + 3.5}
                    textAnchor="middle"
                    fill="#fff"
                    fontSize="9"
                    fontWeight="700"
                    fontFamily="system-ui, sans-serif"
                  >
                    {markerNum}
                  </text>
                </g>
              ) : null}
            </g>
          );
        })}
      </svg>
    </div>
  );
};

InteractiveBodyFigure.propTypes = {
  label: PropTypes.string.isRequired,
  regions: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  markerIndexById: PropTypes.instanceOf(Map).isRequired,
  canEdit: PropTypes.bool,
  printMode: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
};

/** Flat Front/Back map — no nested card; lives inside the parent Section. */
export const WoundBodyMap = ({
  selectedIds = [],
  canEdit = false,
  printMode = false,
  onToggleRegion,
  onClearImage,
}) => {
  const markerIndexById = new Map();
  selectedIds.forEach((id, i) => {
    markerIndexById.set(id, i + 1);
  });
  const unusedNumbers = [];
  for (let n = selectedIds.length + 1; n <= MAX_WOUNDS; n += 1) {
    unusedNumbers.push(n);
  }

  return (
    <div className="w-full">
      <div className="flex flex-col items-center text-center gap-1 mb-2">
        <p className="text-[10px] text-slate-500 leading-snug max-w-md">
          {canEdit && !printMode
            ? "Tap Front or Back to mark wound location(s). Numbers 1–8 map to Wound # below."
            : "Marked wound locations on the figure."}
        </p>
        {selectedIds.length > 0 ? (
          <span className="text-[10px] font-semibold text-red-700">
            {selectedIds.length} marked
          </span>
        ) : null}
      </div>
      {canEdit && !printMode ? (
        <div className="flex justify-center mb-2">
          <button
            type="button"
            onClick={onClearImage}
            className={`h-7 px-3 text-[11px] font-semibold rounded-md border ${PATIENT_THEME.button.outline}`}
          >
            Clear Image
          </button>
        </div>
      ) : null}
      {canEdit && !printMode && unusedNumbers.length > 0 ? (
        <div className="flex flex-wrap justify-center gap-1.5 mb-2">
          {unusedNumbers.map((n) => (
            <span
              key={n}
              className="inline-flex items-center justify-center w-7 h-7 rounded border border-slate-300 bg-white text-[11px] font-semibold text-slate-600"
            >
              {n}
            </span>
          ))}
        </div>
      ) : null}
      <div className="mx-auto flex justify-center items-start gap-8 sm:gap-12 w-full max-w-md">
        <InteractiveBodyFigure
          label="Front"
          regions={WOUND_MAP_FRONT_REGIONS}
          selectedIds={selectedIds}
          markerIndexById={markerIndexById}
          canEdit={canEdit}
          printMode={printMode}
          onToggle={onToggleRegion}
        />
        <InteractiveBodyFigure
          label="Back"
          regions={WOUND_MAP_BACK_REGIONS}
          selectedIds={selectedIds}
          markerIndexById={markerIndexById}
          canEdit={canEdit}
          printMode={printMode}
          onToggle={onToggleRegion}
        />
      </div>
    </div>
  );
};

WoundBodyMap.propTypes = {
  selectedIds: PropTypes.arrayOf(PropTypes.string),
  canEdit: PropTypes.bool,
  printMode: PropTypes.bool,
  onToggleRegion: PropTypes.func.isRequired,
  onClearImage: PropTypes.func.isRequired,
};

const LocationChips = ({ siteIds, canEdit, onRemove }) => {
  if (!siteIds.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5 mb-1.5">
      {siteIds.map((id, index) => {
        const region = getWoundMapRegion(id);
        const label = region?.label || id;
        return (
          <span
            key={id}
            className="inline-flex items-center gap-1 rounded border border-red-200 bg-red-50 text-red-800 text-[11px] font-medium pl-1.5 pr-1 py-0.5"
          >
            <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-red-600 text-white text-[9px] font-bold">
              {index + 1}
            </span>
            {label}
            {canEdit ? (
              <button
                type="button"
                className="ml-0.5 rounded p-0.5 text-red-500 hover:bg-red-100 hover:text-red-800"
                aria-label={`Remove ${label}`}
                onClick={() => onRemove(id)}
              >
                <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden>
                  <path
                    d="M2 2l6 6M8 2L2 8"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            ) : null}
          </span>
        );
      })}
    </div>
  );
};

LocationChips.propTypes = {
  siteIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  canEdit: PropTypes.bool,
  onRemove: PropTypes.func.isRequired,
};

const WoundMapDateField = ({ name, value, canEdit, printMode, onChange }) => {
  if (printMode) {
    return (
      <div className={`${fieldClass} flex items-center`}>
        {displayDateUS(value) || "—"}
      </div>
    );
  }
  return (
    <InputDatePicker
      name={name}
      formatSeparator="/"
      allowManualEntry
      disabled={!canEdit}
      value={formatDateForInput(value) || ""}
      onChange={(event) => onChange(event.target.value)}
      className="w-full"
      inputClassName={`${fieldClass} bg-white`}
    />
  );
};

WoundMapDateField.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.string,
  canEdit: PropTypes.bool,
  printMode: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
};

const NestedCard = ({ title, actions, children }) => (
  <div className="rounded-md border border-indigo-200 overflow-hidden">
    <div
      className={`flex items-center justify-between gap-2 px-3 py-1.5 border-b ${PATIENT_THEME.section.head}`}
    >
      <h4
        className={`text-[10px] font-semibold uppercase tracking-wide ${PATIENT_THEME.section.title}`}
      >
        {title}
      </h4>
      {actions}
    </div>
    <div className="p-3 space-y-3">{children}</div>
  </div>
);

NestedCard.propTypes = {
  title: PropTypes.string.isRequired,
  actions: PropTypes.node,
  children: PropTypes.node,
};

const SelectField = ({ label, value, options, canEdit, onChange, placeholder = "Select" }) => (
  <Field label={label}>
    <select
      className={selectClass}
      value={value || ""}
      disabled={!canEdit}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="">{placeholder}</option>
      {options.map((opt) => (
        <option key={opt} value={opt}>
          {opt}
        </option>
      ))}
    </select>
  </Field>
);

SelectField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.string).isRequired,
  canEdit: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
};

const UnitField = ({ label, value, unit, canEdit, onChange, ariaLabel }) => (
  <Field label={label}>
    <div className="relative">
      <input
        type="text"
        inputMode="decimal"
        className={`${fieldClass} pr-10 tabular-nums`}
        value={value || ""}
        disabled={!canEdit}
        aria-label={ariaLabel || label}
        onChange={(e) => onChange(e.target.value)}
      />
      <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-semibold text-slate-400">
        {unit}
      </span>
    </div>
  </Field>
);

UnitField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  unit: PropTypes.string.isRequired,
  canEdit: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  ariaLabel: PropTypes.string,
};

const OtherTextRow = ({ checked, text, canEdit, onToggle, onText }) => (
  <div className="flex items-center gap-2 min-w-0">
    <label
      className={`inline-flex items-center gap-2 shrink-0 text-[12px] text-slate-700 rounded-md border px-2.5 h-7 ${
        canEdit ? "cursor-pointer" : "cursor-default"
      } ${
        checked
          ? "border-indigo-300 bg-indigo-50/60"
          : "border-slate-200 bg-white"
      }`}
    >
      <input
        type="checkbox"
        className={`${checkboxClass} mt-0 shrink-0`}
        checked={checked}
        disabled={!canEdit}
        onChange={onToggle}
      />
      Other
    </label>
    <input
      type="text"
      className={`${fieldClass} flex-1 min-w-0`}
      value={text || ""}
      disabled={!canEdit || !checked}
      placeholder={checked ? "Describe" : ""}
      onChange={(e) => onText(e.target.value)}
    />
  </div>
);

OtherTextRow.propTypes = {
  checked: PropTypes.bool,
  text: PropTypes.string,
  canEdit: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  onText: PropTypes.func.isRequired,
};

const ClearLink = ({ canEdit, onClear, children = "Clear" }) => {
  if (!canEdit) return null;
  return (
    <button
      type="button"
      className="text-[11px] font-semibold text-red-600 hover:underline"
      onClick={onClear}
    >
      {children}
    </button>
  );
};

ClearLink.propTypes = {
  canEdit: PropTypes.bool,
  onClear: PropTypes.func.isRequired,
  children: PropTypes.node,
};

const CheckboxWithOther = ({
  options,
  values,
  otherText,
  canEdit,
  columns = 5,
  onToggle,
  onOtherText,
}) => {
  const withoutOther = options.filter((opt) => opt !== "Other");
  const selected = Array.isArray(values) ? values : [];
  return (
    <div className="space-y-1.5">
      <CheckboxGrid
        options={withoutOther}
        values={selected}
        canEdit={canEdit}
        onToggle={onToggle}
        columns={columns}
      />
      <OtherTextRow
        checked={selected.includes("Other")}
        text={otherText}
        canEdit={canEdit}
        onToggle={() => onToggle("Other")}
        onText={onOtherText}
      />
    </div>
  );
};

CheckboxWithOther.propTypes = {
  options: PropTypes.arrayOf(PropTypes.string).isRequired,
  values: PropTypes.arrayOf(PropTypes.string),
  otherText: PropTypes.string,
  canEdit: PropTypes.bool,
  columns: PropTypes.number,
  onToggle: PropTypes.func.isRequired,
  onOtherText: PropTypes.func.isRequired,
};

const WoundCareBlock = ({ wound, canEdit, onPatch, onToggleList }) => (
  <NestedCard title="Wound Care Provided">
    <label
      className={`inline-flex items-center gap-2 text-[12px] text-slate-700 ${
        canEdit ? "cursor-pointer" : "cursor-default"
      }`}
    >
      <input
        type="checkbox"
        className={`${checkboxClass} mt-0`}
        checked={Boolean(wound.careNotDueToday)}
        disabled={!canEdit}
        onChange={(e) => onPatch({ careNotDueToday: e.target.checked })}
      />
      Not due today
    </label>

    <div className="space-y-2">
      {[
        ["cleansedWith", "Cleansed with"],
        ["packedWith", "Packed with"],
        ["dressedWith", "Dressed with"],
        ["coveredWith", "Covered with"],
        ["securedWith", "Secured with"],
      ].map(([key, label]) => (
        <Field key={key} label={label}>
          <input
            type="text"
            className={fieldClass}
            value={wound[key] || ""}
            disabled={!canEdit}
            onChange={(e) => onPatch({ [key]: e.target.value })}
          />
        </Field>
      ))}
    </div>

    <div className="border-t border-slate-200 pt-3 space-y-2">
      <Field label="NPWT — Type">
        <input
          type="text"
          className={fieldClass}
          value={wound.npwtType || ""}
          disabled={!canEdit}
          onChange={(e) => onPatch({ npwtType: e.target.value })}
        />
      </Field>
      <div>
        <SubHeading>Foam</SubHeading>
        <CheckboxGrid
          options={NPWT_FOAM_OPTIONS}
          values={wound.npwtFoam}
          canEdit={canEdit}
          onToggle={(opt) => onToggleList("npwtFoam", opt)}
          columns={3}
        />
      </div>
      <Field label="# Pieces">
        <input
          type="text"
          className={fieldClass}
          value={wound.npwtPieces || ""}
          disabled={!canEdit}
          onChange={(e) => onPatch({ npwtPieces: e.target.value })}
        />
      </Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <SelectField
          label="Periwound Protection"
          value={wound.npwtPeriwoundProtection}
          options={NPWT_PERIWOUND_OPTIONS}
          canEdit={canEdit}
          onChange={(v) => onPatch({ npwtPeriwoundProtection: v })}
        />
        <SelectField
          label="Wound Bed"
          value={wound.npwtWoundBed}
          options={NPWT_WOUND_BED_OPTIONS}
          canEdit={canEdit}
          onChange={(v) => onPatch({ npwtWoundBed: v })}
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 items-start">
        <UnitField
          label="Pressure"
          value={wound.npwtPressure}
          unit="mmHG"
          canEdit={canEdit}
          onChange={(v) => onPatch({ npwtPressure: v })}
        />
        <div>
          <div className="flex items-center justify-between gap-2">
            <SubHeading>Pressure Mode</SubHeading>
            {wound.npwtPressureMode ? (
              <ClearLink
                canEdit={canEdit}
                onClear={() => onPatch({ npwtPressureMode: "" })}
              />
            ) : null}
          </div>
          <ExclusiveChoiceGrid
            name={`npwtPressureMode-${wound.id}`}
            options={NPWT_PRESSURE_MODE_OPTIONS}
            value={wound.npwtPressureMode || ""}
            canEdit={canEdit}
            onChange={(opt) => onPatch({ npwtPressureMode: opt })}
            columns={2}
          />
        </div>
      </div>
      <label
        className={`inline-flex items-center gap-2 text-[12px] text-slate-700 ${
          canEdit ? "cursor-pointer" : "cursor-default"
        }`}
      >
        <input
          type="checkbox"
          className={`${checkboxClass} mt-0`}
          checked={Boolean(wound.npwtCanisterChange)}
          disabled={!canEdit}
          onChange={(e) => onPatch({ npwtCanisterChange: e.target.checked })}
        />
        Canister Change
      </label>
    </div>

    <div className="border-t border-slate-200 pt-3">
      <Field label="Patient response to treatment">
        <textarea
          rows={3}
          className={textareaClass}
          value={wound.patientResponse || ""}
          disabled={!canEdit}
          onChange={(e) => onPatch({ patientResponse: e.target.value })}
        />
      </Field>
    </div>
  </NestedCard>
);

WoundCareBlock.propTypes = {
  wound: PropTypes.object.isRequired,
  canEdit: PropTypes.bool,
  onPatch: PropTypes.func.isRequired,
  onToggleList: PropTypes.func.isRequired,
};

const WoundAssessmentCard = ({
  wound,
  index,
  canEdit,
  onPatch,
  onToggleList,
  onRemove,
  canRemove,
}) => (
  <NestedCard
    title={`Wound #${index + 1}`}
    actions={
      canEdit && canRemove ? (
        <button
          type="button"
          className="inline-flex items-center gap-1 text-[11px] font-semibold text-red-600 hover:underline"
          onClick={onRemove}
          aria-label={`Remove wound ${index + 1}`}
        >
          <DeleteIcon size={12} />
          Remove
        </button>
      ) : null
    }
  >
    <Field label="Location">
      <input
        type="text"
        className={fieldClass}
        value={wound.location || ""}
        disabled={!canEdit}
        onChange={(e) => onPatch({ location: e.target.value })}
      />
    </Field>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      <SelectField
        label="Type"
        value={wound.type}
        options={WOUND_TYPE_OPTIONS}
        canEdit={canEdit}
        onChange={(v) => onPatch({ type: v })}
      />
      <SelectField
        label="Stage"
        value={wound.stage}
        options={WOUND_STAGE_OPTIONS}
        canEdit={canEdit}
        onChange={(v) => onPatch({ stage: v })}
      />
    </div>
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
      <UnitField
        label="Length"
        value={wound.length}
        unit="cm"
        canEdit={canEdit}
        onChange={(v) => onPatch({ length: v })}
      />
      <UnitField
        label="Width"
        value={wound.width}
        unit="cm"
        canEdit={canEdit}
        onChange={(v) => onPatch({ width: v })}
      />
      <UnitField
        label="Sq. cm"
        value={wound.sqCm}
        unit="cm"
        canEdit={canEdit}
        onChange={(v) => onPatch({ sqCm: v })}
      />
      <UnitField
        label="Depth"
        value={wound.depth}
        unit="cm"
        canEdit={canEdit}
        onChange={(v) => onPatch({ depth: v })}
      />
    </div>
    <Field label="Undermining">
      <input
        type="text"
        className={fieldClass}
        value={wound.undermining || ""}
        disabled={!canEdit}
        onChange={(e) => onPatch({ undermining: e.target.value })}
      />
    </Field>
    <Field label="Tunneling">
      <input
        type="text"
        className={fieldClass}
        value={wound.tunneling || ""}
        disabled={!canEdit}
        onChange={(e) => onPatch({ tunneling: e.target.value })}
      />
    </Field>

    <div>
      <SubHeading>Surrounding Tissue</SubHeading>
      <CheckboxWithOther
        options={SURROUNDING_TISSUE_OPTIONS}
        values={wound.surroundingTissue}
        otherText={wound.surroundingTissueOther}
        canEdit={canEdit}
        columns={5}
        onToggle={(opt) => onToggleList("surroundingTissue", opt)}
        onOtherText={(v) => onPatch({ surroundingTissueOther: v })}
      />
    </div>

    <Field label="Wound Bed Color">
      <input
        type="text"
        className={fieldClass}
        value={wound.woundBedColor || ""}
        disabled={!canEdit}
        onChange={(e) => onPatch({ woundBedColor: e.target.value })}
      />
    </Field>

    <div>
      <SubHeading>Wound Bed Tissue</SubHeading>
      <CheckboxWithOther
        options={WOUND_BED_TISSUE_OPTIONS}
        values={wound.woundBedTissue}
        otherText={wound.woundBedTissueOther}
        canEdit={canEdit}
        columns={4}
        onToggle={(opt) => onToggleList("woundBedTissue", opt)}
        onOtherText={(v) => onPatch({ woundBedTissueOther: v })}
      />
    </div>

    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
      <SelectField
        label="Drainage Type"
        value={wound.drainageType}
        options={DRAINAGE_TYPE_OPTIONS}
        canEdit={canEdit}
        onChange={(v) => onPatch({ drainageType: v })}
      />
      <SelectField
        label="Drainage Amount"
        value={wound.drainageAmount}
        options={DRAINAGE_AMOUNT_OPTIONS}
        canEdit={canEdit}
        onChange={(v) => onPatch({ drainageAmount: v })}
      />
      <Field label="Drainage Odor">
        <input
          type="text"
          className={fieldClass}
          value={wound.drainageOdor || ""}
          disabled={!canEdit}
          onChange={(e) => onPatch({ drainageOdor: e.target.value })}
        />
      </Field>
    </div>

    <WoundCareBlock
      wound={wound}
      canEdit={canEdit}
      onPatch={onPatch}
      onToggleList={onToggleList}
    />
  </NestedCard>
);

WoundAssessmentCard.propTypes = {
  wound: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  canEdit: PropTypes.bool,
  onPatch: PropTypes.func.isRequired,
  onToggleList: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  canRemove: PropTypes.bool,
};

/**
 * Shared Wound Assessment block for all Nurse Eval & Visit Notes forms.
 * Surgical wound is always shown; identified wounds are repeatable (max 8).
 */
const WoundPresentSection = ({
  values,
  canEdit,
  printMode = false,
  onChange,
  onToggleList,
}) => {
  const woundYes = values.woundPresent === "Yes";
  const siteIds = Array.isArray(values.woundMapSites)
    ? values.woundMapSites
    : EMPTY_LIST;
  const wounds = Array.isArray(values.wounds) ? values.wounds : EMPTY_LIST;
  const incisionClosed = Array.isArray(values.incisionClosedWith)
    ? values.incisionClosedWith
    : EMPTY_LIST;

  const applyFields = useCallback(
    (patch) => {
      Object.entries(patch).forEach(([key, next]) => onChange(key, next));
    },
    [onChange],
  );

  const resetIdentifiedWounds = useCallback(
    (woundPresent = "") => {
      applyFields({
        ...emptyIdentifiedWoundFields(),
        woundPresent,
      });
    },
    [applyFields],
  );

  const applySites = useCallback(
    (nextSites) => {
      onChange("woundMapSites", nextSites);
      onChange("woundLocation", woundLocationFromSites(nextSites));
    },
    [onChange],
  );

  const handleToggleRegion = useCallback(
    (regionId) => {
      const adding = !siteIds.includes(regionId);
      if (adding && siteIds.length >= MAX_WOUNDS) return;
      const nextSites = toggleWoundMapSite(siteIds, regionId);
      applySites(nextSites);

      if (!adding) return;
      const region = getWoundMapRegion(regionId);
      const idx = nextSites.indexOf(regionId);
      const nextWounds = [...wounds];
      while (nextWounds.length <= idx) {
        nextWounds.push(emptyWoundEntry());
      }
      if (region && !String(nextWounds[idx].location || "").trim()) {
        nextWounds[idx] = { ...nextWounds[idx], location: region.label };
      }
      onChange("wounds", nextWounds);
    },
    [applySites, onChange, siteIds, wounds],
  );

  const handleClearImage = useCallback(() => {
    applySites([]);
    onChange("wounds", [emptyWoundEntry()]);
  }, [applySites, onChange]);

  const handleWoundPresent = (opt) => {
    if (opt === "Yes") {
      onChange("woundPresent", "Yes");
      if (!wounds.length) onChange("wounds", [emptyWoundEntry()]);
      return;
    }
    resetIdentifiedWounds(opt || "");
  };

  const patchWound = (id, patch) => {
    onChange(
      "wounds",
      wounds.map((row) => {
        if (row.id !== id) return row;
        const next = { ...row, ...patch };
        if ("length" in patch || "width" in patch) {
          const area = computeWoundAreaSqCm(next.length, next.width);
          if (area) next.sqCm = area;
        }
        return next;
      }),
    );
  };

  const toggleWoundList = (id, key, opt) => {
    const row = wounds.find((w) => w.id === id);
    patchWound(id, { [key]: toggleInWoundList(row?.[key], opt) });
  };

  const addWound = () => {
    if (wounds.length >= MAX_WOUNDS) return;
    onChange("wounds", [...wounds, emptyWoundEntry()]);
  };

  const removeWound = (id) => {
    onChange(
      "wounds",
      wounds.filter((row) => row.id !== id),
    );
  };

  return (
    <div className="space-y-3">
      <div>
        <SubHeading>
          Surgical wound (complete for closed surgical wounds only)
        </SubHeading>
        <div className="space-y-2">
          <Field label="Location">
            <input
              type="text"
              className={fieldClass}
              value={values.surgicalWoundLocation || ""}
              disabled={!canEdit}
              onChange={(e) => onChange("surgicalWoundLocation", e.target.value)}
            />
          </Field>
          <div>
            <SubHeading>Incision closed with</SubHeading>
            <CheckboxWithOther
              options={INCISION_CLOSED_WITH_OPTIONS}
              values={incisionClosed}
              otherText={values.incisionClosedOther}
              canEdit={canEdit}
              columns={5}
              onToggle={(opt) => onToggleList("incisionClosedWith", opt)}
              onOtherText={(v) => onChange("incisionClosedOther", v)}
            />
          </div>
          <Field label="Incision size/appearance">
            <input
              type="text"
              className={fieldClass}
              value={values.incisionSizeAppearance || ""}
              disabled={!canEdit}
              onChange={(e) =>
                onChange("incisionSizeAppearance", e.target.value)
              }
            />
          </Field>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between gap-2">
          <SubHeading>Wound(s) identified?</SubHeading>
          {values.woundPresent ? (
            <ClearLink
              canEdit={canEdit && !printMode}
              onClear={() => resetIdentifiedWounds("")}
            />
          ) : null}
        </div>
        <ExclusiveChoiceGrid
          name="woundPresent"
          options={WOUND_YES_NO_OPTIONS}
          value={values.woundPresent || ""}
          canEdit={canEdit}
          onChange={handleWoundPresent}
          columns={2}
        />
        {woundYes ? (
          <p className="mt-1 text-[10px] text-slate-500">
            (complete wound information below)
          </p>
        ) : null}
      </div>

      {woundYes ? (
        <>
          <div className="space-y-3">
            <WoundBodyMap
              selectedIds={siteIds}
              canEdit={canEdit}
              printMode={printMode}
              onToggleRegion={handleToggleRegion}
              onClearImage={handleClearImage}
            />
            <Field label="Marked locations">
              <LocationChips
                siteIds={siteIds}
                canEdit={canEdit && !printMode}
                onRemove={handleToggleRegion}
              />
              <input
                type="text"
                className={fieldClass}
                value={values.woundLocation || ""}
                disabled={!canEdit}
                placeholder={
                  siteIds.length
                    ? "From body map — edit if needed"
                    : "Mark on body map, or type location"
                }
                aria-label="Wound location summary"
                onChange={(e) => {
                  const text = e.target.value;
                  onChange("woundLocation", text);
                  if (!String(text || "").trim()) {
                    onChange("woundMapSites", []);
                  }
                }}
              />
            </Field>
          </div>

          <div className="space-y-3">
            {wounds.map((wound, index) => (
              <WoundAssessmentCard
                key={wound.id || `wound-${index}`}
                wound={wound}
                index={index}
                canEdit={canEdit}
                onPatch={(patch) => patchWound(wound.id, patch)}
                onToggleList={(key, opt) => toggleWoundList(wound.id, key, opt)}
                onRemove={() => removeWound(wound.id)}
                canRemove={wounds.length > 1 || Boolean(wound.location || wound.type)}
              />
            ))}
          </div>

          {canEdit && !printMode && wounds.length < MAX_WOUNDS ? (
            <div className="flex justify-end">
              <button
                type="button"
                onClick={addWound}
                className={`inline-flex items-center gap-1.5 h-8 px-3 text-xs font-semibold rounded-md border ${PATIENT_THEME.button.outline}`}
              >
                <PlusIcon size={13} />
                Add Wound
              </button>
            </div>
          ) : null}

          <div>
            <SubHeading>Wound Care Education Provided?</SubHeading>
            <ExclusiveChoiceGrid
              name="woundCareEducation"
              options={WOUND_YES_NO_OPTIONS}
              value={values.woundCareEducation || ""}
              canEdit={canEdit}
              onChange={(opt) => onChange("woundCareEducation", opt)}
              columns={2}
            />
          </div>

          <Field label="Wound Map Notes">
            <textarea
              rows={2}
              className={textareaClass}
              value={values.woundMapNotes || ""}
              disabled={!canEdit}
              placeholder="Describe findings for the marked sites…"
              onChange={(e) => onChange("woundMapNotes", e.target.value)}
            />
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <Field label="Patient ID">
              <input
                type="text"
                className={fieldClass}
                value={values.woundMapPatientId || ""}
                disabled={!canEdit}
                onChange={(e) => onChange("woundMapPatientId", e.target.value)}
              />
            </Field>
            <Field label="Map Date">
              <WoundMapDateField
                name="woundMapDate"
                value={values.woundMapDate || ""}
                canEdit={canEdit}
                printMode={printMode}
                onChange={(v) => onChange("woundMapDate", v)}
              />
            </Field>
            <Field label="Clinician">
              <input
                type="text"
                className={fieldClass}
                value={values.woundMapClinician || ""}
                disabled={!canEdit}
                onChange={(e) => onChange("woundMapClinician", e.target.value)}
              />
            </Field>
          </div>
        </>
      ) : null}
    </div>
  );
};

WoundPresentSection.propTypes = {
  values: PropTypes.object.isRequired,
  canEdit: PropTypes.bool,
  printMode: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onToggleList: PropTypes.func.isRequired,
};

export default WoundPresentSection;
